// Utility helpers for saving generated content (carousel slides, reel
// scenes) to the user's device or sharing via the Web Share API.
//
// "Save" path = plain browser download (silent). "Share" path = native
// share sheet (opt-in via opts.useShare). The share sheet is required on
// iOS Safari for the user to land files inside their Photos library
// instead of the Files app, but it's intrusive when exporting 10 slides
// at once — so the default is the silent download.

/**
 * Save a Blob to the user's device. By default uses a plain browser
 * download (silent, lands in the system Downloads/Files app).
 *
 * Pass `opts.useShare = true` to opt into the Web Share API on mobile,
 * which surfaces the native share sheet (and lets the user pick "Save
 * to Photos" / "Save to Gallery"). The trade-off: every file shown
 * adds a confirmation dialog, which is intrusive for bulk exports.
 *
 * Returns true when the share or download was initiated, false when canceled.
 */
export async function saveBlobToGallery(blob, filename, opts = {}) {
  const useShare = !!opts.useShare;
  const title = opts.title || filename;
  const text = opts.text || "";

  if (useShare) {
    const file = new File([blob], filename, { type: blob.type || "image/png" });
    try {
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare({ files: [file] }) &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share({ files: [file], title, text });
          return true;
        } catch (e) {
          if (e?.name === "AbortError") return false;
          // fall through to download
        }
      }
    } catch {
      /* falls back to plain download */
    }
  }

  // Plain download: browser saves the file to the standard location.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}

/**
 * Trigger the same share/save behavior for a base64 image string.
 */
export async function saveBase64ToGallery(b64, filename, mime = "image/png", opts = {}) {
  // Convert base64 → Blob without going through a giant data URL (avoids
  // the ~50MB string limit some browsers hit on long carousels).
  const byteString = atob(b64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    bytes[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mime });
  return saveBlobToGallery(blob, filename, opts);
}

/** Heuristic: are we on a device where the native gallery save is useful? */
export function isMobileWithShare() {
  if (typeof navigator === "undefined") return false;
  const ua = (navigator.userAgent || "").toLowerCase();
  const isMobile = /android|iphone|ipad|ipod|mobile|webos/.test(ua);
  return isMobile && typeof navigator.canShare === "function";
}
